import {
  BracketMatch,
  getRoundName,
} from '@plvs/respawn/components/bracket/Bracket'
import { groupBy, toPairs } from 'ramda'
import {
  BracketRoundType,
  BracketSlotType,
  BracketTeamMatchStatus,
  EsportRating,
  GetPhaseBracketQuery,
} from '@plvs/graphql'
import {
  NxAvatarSize,
  NxUserAvatar,
} from '@plvs/respawn/features/avatar/NxUserAvatar'
import { UnknownAvatar } from '@playvs-inc/nexus-icons'
import React from 'react'
import { Location } from '@plvs/client-data/models'
import { ClassNameMap } from '@material-ui/styles'
import { getLinks } from '@plvs/respawn/containers/bracket/BracketContainer.helpers'

export type Matches = NonNullable<
  GetPhaseBracketQuery['getBracketByPhaseId']
>['bracketMatches']

export enum BracketPhase {
  Upper = 'upper',
  Lower = 'lower',
  Finals = 'finals',
}

function composeFinalRounds(rounds: BracketMatch[][]): BracketMatch[][] {
  const finalUpperRound = rounds.find((round) =>
    round.some((match) => match.roundType === BracketRoundType.UpperFinal)
  )?.[0]
  const finalLowerRound = rounds.find((round) =>
    round.some((match) => match.roundType === BracketRoundType.LowerFinal)
  )?.[0]
  const finalRound = rounds.find((round) =>
    round.some((match) => match.roundType === BracketRoundType.Final)
  )?.[0]
  const grandFinalRound = rounds.find((round) =>
    round.some((match) => match.roundType === BracketRoundType.GrandFinal)
  )?.[0]

  const processedFinalRounds = [
    [finalUpperRound, finalLowerRound],
    [finalRound],
  ]

  if (grandFinalRound?.matchId) {
    processedFinalRounds.push([grandFinalRound])
  }

  return processedFinalRounds as BracketMatch[][]
}

function filterOutEmptyRounds(rounds: BracketMatch[][]): BracketMatch[][] {
  return rounds.filter((round) => {
    return round.length > 0
  })
}

export function getRoundsFromMatches({
  matches,
  isDoubleElimination,
  hasSeries,
  isYouthProgram,
  rating,
  isLoggedIn,
  appLocation,
  styles,
}: {
  matches: Matches
  hasSeries: boolean
  isYouthProgram: boolean
  rating: EsportRating
  isLoggedIn: boolean
  appLocation: Location
  styles: ClassNameMap<'icon'>
  isDoubleElimination: boolean
}): {
  processedRounds: BracketMatch[][]
  processedUpperRounds: BracketMatch[][]
  processedLowerRounds: BracketMatch[][]
  processedFinalRounds: BracketMatch[][]
} {
  const processedRounds: BracketMatch[][] = []
  const processedUpperRounds: BracketMatch[][] = []
  const processedLowerRounds: BracketMatch[][] = []
  const rounds = toPairs(
    groupBy((match) => match.roundNumber.toString(), matches)
  )

  rounds.forEach((roundGrouped, roundIndex) => {
    const processRound: BracketMatch[] = []
    const processUpperRound: BracketMatch[] = []
    const processLowerRound: BracketMatch[] = []
    const round = roundGrouped[1]
    round.forEach((match) => {
      const isLower = match.slotType === BracketSlotType.Lower
      const isUpper = match.slotType === BracketSlotType.Upper
      const isFinals = match.roundType === BracketRoundType.Final
      const isGrandFinals = match.roundType === BracketRoundType.GrandFinal
      const isSemiFinals = match.roundType === BracketRoundType.SemiFinal
      const shouldShowGrandFinals = isGrandFinals && match.matchId

      const processed = {
        id: match.bracketIndex ?? '',
        matchId: match.matchId ?? '',
        isUpper,
        isFinals,
        roundType: match.roundType,
        name: `${getRoundName(
          roundIndex + 1,
          isDoubleElimination,
          !isLower,
          isFinals,
          match.roundType
        )} - Match ${match.ordinalNumber + 1}`,
        nextMatchId: match.nextBracketIndex,
        roundIndex,
        startTime: match.match?.scheduledStartsAt ?? match.slot?.startsAt ?? '',
        participants: match.bracketTeams?.map((t) => {
          const result = match.match?.matchResults?.find(
            (x) => x.teamId === t.teamId
          )
          const score = hasSeries ? result?.seriesWon : result?.gamesWon
          const { schoolLink, teamLink, matchLink } = getLinks(
            appLocation,
            isLoggedIn,
            t.team?.school?.slug ?? undefined,
            t.schoolId ?? undefined,
            t.teamId ?? undefined,
            match.matchId ?? undefined
          )
          const hideSchoolDetails =
            isYouthProgram ||
            rating === EsportRating.Restricted ||
            t.matchStatus === BracketTeamMatchStatus.Bye ||
            t.matchStatus === BracketTeamMatchStatus.Tbd
          const isWinner = t.matchStatus === BracketTeamMatchStatus.Win
          const isLoser = t.matchStatus === BracketTeamMatchStatus.Loss
          return {
            id: t.teamId ?? '',
            teamId: t.teamId ?? '',
            seed: t.seed,
            status: t.matchStatus,
            displayName:
              t.matchStatus === BracketTeamMatchStatus.Bye
                ? 'Bye'
                : t.team?.name ?? 'TBD',
            picture:
              t.team?.avatarUrl && !hideSchoolDetails ? (
                <NxUserAvatar
                  avatarUrl={t.team?.avatarUrl}
                  hashId=""
                  size={NxAvatarSize.XS}
                />
              ) : (
                <UnknownAvatar className={styles.icon} />
              ),
            schoolName:
              t.team?.school?.name && !(hideSchoolDetails || t.isDeleted)
                ? t.team?.school?.name
                : '',
            score: score ?? null,
            schoolId: t.team?.school?.id ?? '',
            isDeleted: t.isDeleted,
            schoolLink,
            teamLink,
            matchLink,
            isWinner,
            isLoser,
          }
        }),
      }

      if (shouldShowGrandFinals || !isGrandFinals) {
        processRound.push(processed)

        if (isLower) {
          processLowerRound.push(processed)
        } else if (isUpper && !isFinals && !isGrandFinals) {
          processUpperRound.push(processed)
        }

        if (isSemiFinals && isLower) {
          processLowerRound.push(processed)
        }
      }
    })
    processedRounds.push(processRound)
    processedUpperRounds.push(processUpperRound)
    processedLowerRounds.push(processLowerRound)
  })

  const filteredRounds = filterOutEmptyRounds(processedRounds)
  const filteredUpperRounds = filterOutEmptyRounds(processedUpperRounds)
  const filteredLowerRounds = filterOutEmptyRounds(processedLowerRounds)

  return {
    processedRounds: filteredRounds,
    processedUpperRounds: filteredUpperRounds,
    processedLowerRounds: filteredLowerRounds,
    processedFinalRounds: composeFinalRounds(filteredRounds),
  }
}
